/**
 * useShellSyncNotices - what the sync layer says to the person at the screen.
 *
 * - A refused WRITE (since v8.199.17 each carries its own message) is a
 *   toast. Listeners no longer die - they retry forever - so an outage
 *   speaks through ONE honest line when the connection is lost and its
 *   counterpart when delivery resumes, instead of the old permanent-death
 *   toast that blamed a session which usually still existed.
 * - Being REMOVED from a session is not an error to retry - it is an answer.
 *   The listeners are already refused, so the session on screen is a still
 *   photograph: say so and return to the lobby.
 *
 * Returns `onSyncError(message)`, the toast the sync hook reports through.
 * Extracted from DeathWatchSimulator; the messages are unchanged.
 */
import { useEffect, useCallback } from 'react';
import * as multiplayer from '../multiplayer';

const REFUSED = 'A write was refused - your last change may not have reached the server.';

/** The toast for a connection state change. */
export function connectionNotice(state) {
  return state === 'lost'
    ? ['Connection to the server lost - reconnecting…', { type: 'error' }]
    : ['Reconnected - syncing.', { type: 'success' }];
}

/**
 * @param {Function} pushToast - (message, options) from the toast context
 * @param {Function} setSession - the app's session setter; a revoked seat returns to the lobby
 */
export default function useShellSyncNotices(pushToast, setSession) {
  useEffect(() => {
    multiplayer.setListenerFailureHandler((label) => pushToast(label || REFUSED, { type: 'error' }));
    multiplayer.setConnectionNoticeHandler((state) => pushToast(...connectionNotice(state)));
    return () => {
      multiplayer.setListenerFailureHandler(null);
      multiplayer.setConnectionNoticeHandler(null);
    };
  }, [pushToast]);
  useEffect(() => {
    multiplayer.setSessionRevokedHandler(() => {
      pushToast('You are no longer in this session. The GM may have removed you.', { type: 'error' });
      setSession({ id: null, code: null, isGm: false, playerId: null });
    });
    return () => multiplayer.setSessionRevokedHandler(null);
  }, [pushToast, setSession]);
  return useCallback((message) => pushToast(message, { type: 'error' }), [pushToast]);
}
